import { Alert, Box } from "@mui/material";

import { useGetAudienceReach, type AudienceReachMedium } from "@/api/audiences";
import { PeopleAltOutlinedIcon, WarningAmberRoundedIcon } from "@/icons";
import { formatNumber } from "@/utils/metrics";

export interface AudienceReachBannerViewProps {
  /** Primary recipient count, rendered bold. */
  count: number;
  /**
   * Pre-filter count, rendered struck-through beside the primary count. Only
   * shown when provided and greater than `count` (i.e. some were filtered out).
   */
  originalCount?: number;
  /** When greater than zero, renders a "removed ~{n} unengaged profiles" line. */
  removedCount?: number;
  /** Leading label before the count, e.g. "Estimated audience:". */
  leadingText?: string;
  "data-testid"?: string;
}

/**
 * Presentational recipient-count banner shared by the compose flow (fed a live
 * reach estimate) and the completed/sent view (fed the send-time snapshot). Keeps
 * the count/formatting rendering in one place so both surfaces look identical.
 */
export function AudienceReachBannerView({
  count,
  originalCount,
  removedCount,
  leadingText,
  "data-testid": testId,
}: AudienceReachBannerViewProps) {
  const showOriginal = originalCount != null && originalCount > count;
  const showRemoved = removedCount != null && removedCount > 0;

  return (
    <Alert
      severity="warning"
      icon={<PeopleAltOutlinedIcon fontSize="small" />}
      data-testid={testId}
      sx={{ fontSize: "small" }}
    >
      {leadingText ? `${leadingText} ` : null}
      <Box component="span" sx={{ fontWeight: 600 }}>
        {formatNumber(count)}
      </Box>
      {showOriginal ? (
        <Box
          component="span"
          sx={{
            textDecoration: "line-through",
            color: "text.secondary",
            ml: 1,
          }}
        >
          {formatNumber(originalCount)}
        </Box>
      ) : null}{" "}
      profiles
      {showRemoved ? (
        <Box sx={{ mt: 2 }}>
          removed ~{formatNumber(removedCount)} unengaged profiles
        </Box>
      ) : null}
    </Alert>
  );
}

export interface AudienceReachBannerProps {
  siteId: string;
  /** Selected audience ids; the estimate is the deduplicated union of these. */
  audienceIds: string[];
  /** Smart Audience toggle state. Drives the local trim scaling (see below). */
  reachOptimization: boolean;
  /** Trim percentage (1–99); scales the raw union locally when the toggle is on. */
  reachOptimizationPercentage: number;
  /**
   * Excluded ("do not send to") audience ids. The backend subtracts their
   * members from the reach estimate, so the count reflects what will actually
   * be sent.
   */
  excludedAudienceIds?: string[];
  /**
   * The broadcast's channel; gates the estimate to channel-eligible profiles
   * (email-eligible vs text-eligible). Part of the query key, so switching
   * channel refetches.
   */
  medium: AudienceReachMedium;
}

/**
 * The estimated reach (deduplicated union of the selected audiences) shown
 * beneath the audience picker. When any selected audience is still being
 * processed by the segment worker, a warning replaces the estimate because the
 * union is incomplete. Renders nothing when no audiences are selected.
 */
export function AudienceReachBanner({
  siteId,
  audienceIds,
  reachOptimization,
  reachOptimizationPercentage,
  excludedAudienceIds,
  medium,
}: AudienceReachBannerProps) {
  // Fetch the RAW (untrimmed) union once. The union count is backend-owned
  // Valkey-bitmap data that does not depend on the trim %, so we request it with
  // reachOptimization=false and scale locally below. Passing false also keeps
  // the react-query key free of the toggle/percentage, so dragging the slider
  // never refetches — only an audience/exclusion/channel change does.
  const reachQuery = useGetAudienceReach(
    siteId,
    audienceIds,
    false,
    excludedAudienceIds,
    medium,
  );
  const reach = reachQuery.data;
  const pendingAudiences = reach?.pending ?? [];
  const hasPending = pendingAudiences.length > 0;

  if (audienceIds.length === 0) {
    return null;
  }

  if (hasPending) {
    const names = pendingAudiences.map((a) => a.name).join(", ");
    return (
      <Alert
        severity="warning"
        icon={<WarningAmberRoundedIcon fontSize="small" />}
        data-testid="smart-audience-pending-warning"
      >
        {names} {pendingAudiences.length === 1 ? "is" : "are"} still processing
        — sending now will miss recipients. Wait for processing to finish for
        the full{" "}
        {pendingAudiences.length === 1
          ? "audience and estimate"
          : "audiences and estimates"}
        .
      </Alert>
    );
  }

  if (reach) {
    // Trim scaling is trivial arithmetic on the FE-owned slider %, applied to
    // the raw union so the estimate updates live as the slider drags. removed=0
    // (toggle off) degrades cleanly — the view hides the original + removed line.
    const raw = reach.total;
    const removed = reachOptimization
      ? Math.round((raw * reachOptimizationPercentage) / 100)
      : 0;
    return (
      <AudienceReachBannerView
        count={raw - removed}
        originalCount={reachOptimization ? raw : undefined}
        removedCount={reachOptimization ? removed : undefined}
        leadingText="Estimated audience:"
        data-testid="smart-audience-estimate"
      />
    );
  }

  if (reachQuery.isError) {
    return (
      <Alert
        severity="error"
        icon={<WarningAmberRoundedIcon fontSize="small" />}
        data-testid="smart-audience-estimate-error"
      >
        Couldn&apos;t load the audience estimate.
      </Alert>
    );
  }

  return (
    <Alert
      severity="info"
      icon={<PeopleAltOutlinedIcon fontSize="small" />}
      data-testid="smart-audience-estimate-loading"
    >
      Estimating audience size…
    </Alert>
  );
}
